<template>
  <v-dialog
      fullscreen
      eager
      v-model="dialog"
      hide-overlay
      content-class="process-result-dialog form-dialog"
  >
    <LoadingDialog ref="loading" :loader-msg="$t('components:process-result-dialog.generate-load')"/>
    <v-card elevation="0" class="result-card">
      <v-card-text class="pa-0">
        <v-row no-gutters justify="center">

          <v-col cols="12" class="text-right d-block d-md-none">
            <v-btn icon @click="closeDialog" class="mr-3 mt-3">
              <v-icon large color="primary">close</v-icon>
            </v-btn>
          </v-col>

          <v-col cols="10" md="9" class="pt-5 pt-md-10">
            <v-form ref="form" v-show="!isFetchingProcessResult">

            <!-- Quick-Overview CardBar -->
            <v-row justify="center" class="mb-3">
              <v-col cols="12" md="10" class="pa-0">
                <TaskCard
                    :taskName="processResult.processName"
                    :additionalNames="getAdditionalNames(processResult)"
                    :timestamp="finishedOn"
                    :taskStatus="processResult.success ? 'result-success' : 'result-failed'"
                    :accepted="true"
                    :username="processResult.username"
                />
              </v-col>
            </v-row>

            <!-- More specific Overview-Card -->
            <v-row justify="center" >
              <v-col cols="12" sm="6" md="5" class="overview-card pa-5">
                <!-- Section General -->
                <h4 class="primary--text font-weight-medium">{{ $t('common:general') }}</h4>

                <!-- Process Name -->
                <LabelField
                    :label="$t('common:process')"
                    :value="processResult.processName"
                />

                <!-- Facility Name -->
                <LabelField
                    :label="$t('common:facility')"
                    :value="processResult.facilityName"
                />

                <!-- Done By User -->
                <LabelField
                    :label="$t('components:process-result-dialog.labels.done-by-user')"
                    :value="processResult.username"
                />

                <!-- Done on Date -->
                <LabelField
                    :label="$t('components:process-result-dialog.labels.done-on-date')"
                    :value="computedDateFormatted"
                />

                <LabelField
                    :label="$t('components:process-result-dialog.labels.ride-count')"
                    :value="processResult.rideCount ? processResult.rideCount : '-'"
                />

                <LabelField
                    :label="$t('components:process-result-dialog.labels.file-number')"
                    :value="processResult.fileNumber ? processResult.fileNumber : '-'"
                />

                <DataTable
                    v-if="processResult.contactedUsersNames && processResult.contactedUsersNames.length > 0"
                    :enabledSearch="false"
                    :headers="userHeaders"
                    :data="processResult.contactedUsersNames"
                />
              </v-col>

              <v-col cols="12" sm="6" md="5" class="overview-card pa-5">
                <!-- Specific Informations -->
                <h4 class="primary--text font-weight-medium">{{ $t('components:process-result-dialog.titles.specific') }}</h4>
                <!-- GPS check -->
                <v-checkbox
                    disabled
                    v-model="processResult.gpsChecked"
                    :label="$t('components:process-result-dialog.labels.gps-check')"
                    color="primary"
                />

                <!-- Manually added -->
                <v-checkbox
                    disabled
                    v-model="processResult.manuallyAdded"
                    :label="$t('components:process-result-dialog.labels.manually-added')"
                    color="primary"
                />

                <v-checkbox
                    disabled
                    color="secondary"
                    v-model="processResult.requiredSignature"
                    :label="$t('common:gets-signed')"
                />
              </v-col>
            </v-row>

            <!-- ProcessStepResults Panels -->
            <v-row justify="center">
              <v-col cols="12" md="10" class="px-0">
                <h4 class="primary--text font-weight-medium mt-5">{{ $t('components:process-result-dialog.titles.overview') }}</h4>
                <v-expansion-panels tile flat v-model="openedPanel">
                  <v-expansion-panel v-for="item in restProcessStepResults" :key="item.id" class="mb-2">

                    <!-- Panel Header -->
                    <v-expansion-panel-header :class="'pa-3 border-left-success--' + item.success" @click="loadProcessStepResultDocus(item)">
                      <v-row justify="space-between" align="center">
                        <v-col class="py-0">
                          <v-row no-gutters>
                            <!-- Finished Icon -->
                            <v-icon large :color="item.success ? 'scaleGreen' : 'scaleRed'">check_circle_outline</v-icon>
                            <!-- Process Step Result name -->
                            <h6 class="mb-0 pt-2 ml-2 greyDark--text">{{ item.name }}</h6>
                          </v-row>
                        </v-col>
                        <v-col cols="12" sm="4" md="3" lg="2" class="py-sm-0 text-sm-right mr-sm-5">
                          <!-- Status Badge -->
                          <v-chip :color="item.success ? 'scaleGreen' : 'scaleRed'" class="ml-3" small>
                            <span class="font-weight-bold white--text">
                              {{ taskStatusText(item) }}
                            </span>
                          </v-chip>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-header>

                    <!-- Panel Body -->
                    <v-expansion-panel-content class="mt-2">
                      <p>{{ item.description }}</p>

                      <!-- Case stepResult failed -->
                      <div v-if="!item.success">

                        <!-- ProcessStepResult success checkbox -->
                        <v-checkbox
                            v-if="processResult.updateable"
                            v-model="item.success"
                            :label="$t('common:success')"
                            color="secondary"
                        />

                        <!-- Answer -->
                        <h6 class="font-weight-bold greyDark--text mb-2">{{ $t('common:answer') }}</h6>
                        <v-text-field
                            v-if="processResult.updateable"
                            outlined
                            clearable
                            v-model="item.answer"
                            hide-details
                        />
                        <p v-else>{{ item.answer }}</p>

                        <v-row v-if="canHaveComment(item)" justify="space-between">
                          <!-- Comment/Additional answer -->
                          <v-col cols="12" md="5">
                            <h6 class="font-weight-bold greyDark--text mb-2 mt-5">{{ $t('common:comment') }}</h6>
                            <p v-if="item.additionalAnswer && !processResult.updateable">{{ item.additionalAnswer }}</p>

                            <!-- Process Step Result Additional Answer -->
                            <!-- will be toggled if 'updateable' is true or false -->
                            <v-text-field
                                v-else-if="processResult.updateable"
                                outlined
                                clearable
                                hide-details
                                v-model="item.additionalAnswer"
                            />
                          </v-col>
                          <v-col cols="12" md="5">
                            <h6 class="font-weight-bold greyDark--text mb-2 mt-5">{{ $t('components:process-result-dialog.subtitles.deadline') }}</h6>
                            <p v-if="item.defectDeadline && !processResult.updateable">{{ item.defectDeadline }}</p>

                            <v-text-field
                                v-else-if="processResult.updateable"
                                outlined
                                clearable
                                hide-details
                                v-model="item.defectDeadline"
                            />
                          </v-col>
                        </v-row>
                        <div v-else>
                          <h6 class="font-weight-bold greyDark--text mb-2 mt-5">{{ $t('components:process-result-dialog.subtitles.deadline') }}</h6>
                          <p v-if="item.defectDeadline && !processResult.updateable">{{ item.defectDeadline }}</p>

                          <v-text-field
                              v-else-if="processResult.updateable"
                              outlined
                              clearable
                              hide-details
                              v-model="item.defectDeadline"
                          />
                        </div>

                        <!-- Uploaded Documentations -->
                        <div v-if="documentationTable.length > 0">
                          <h6 class="font-weight-bold greyDark--text mb-2 mt-5">{{ $t('common:documentation_plural') }}</h6>
                          <div class="d-flex flex-column flex-sm-row flex-sm-wrap">
                            <div v-for="docu in documentationTable" :key="docu.id">
                              <pdf
                                  ref="pdf"
                                  v-if="docu.fileType === 'application/pdf'"
                                  :src="docu.src"
                                  class="docu-preview mt-5 mt-sm-0 mr-sm-5"
                                  v-on:click.native="viewDocumentation(docu)"
                              >
                              </pdf>

                              <img
                                  v-else
                                  id="image"
                                  :src="docu.src"
                                  class="docu-preview mt-5 mt-sm-0 mr-sm-5"
                                  @click="viewDocumentation(docu)"
                              />
                            </div>
                          </div>
                        </div>

                        <!-- Reason why step failed -->
                        <div class="mt-5" v-if="item.failureText">
                          <h6 class="font-weight-bold greyDark--text mb-2">{{ $t('components:process-result-dialog.subtitles.failure-reason') }}</h6>
                          <p>{{ item.failureText }}</p>
                        </div>
                      </div>

                      <!-- Case stepResult success -->
                      <div v-else-if="item.success && processResult.updateable">
                        <v-checkbox
                            v-model="item.success"
                            :label="$t('common:success')"
                            color="secondary"
                        />
                      </div>

                      <!-- Actions for failed process step -->
                      <div v-if="processResult.updateable" class="mt-5 mb-3">
                        <BaseButton
                            color="primary"
                            :btnText="$t('buttons:save-changes')"
                            @clickedButton="updateProcessStepResult(item)"
                        />

                        <DirtyConfirmButtonDialog
                            ref="dirtyDialog"
                            :btn-text="$t('buttons:cancel')"
                            color="grey"
                            :dialog-title="$t('dialog:dirty-save-btn-title')"
                            :dialog-text="$t('dialog:dirty-save-btn-dialog')"
                            :button1-text="$t('dialog:dirty-save-btn-1-text')"
                            :button2-text="$t('dialog:dirty-save-btn-2-text')"
                            @clickedButton="openedPanel = null"
                            hideOverlay
                        />
                      </div>
                    </v-expansion-panel-content>

                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>
            <v-row justify="center">
              <v-col cols="12" md="10" class="px-0">
                <BaseButton
                    color="scaleGreen"
                    :btn-text="$t('buttons:generate-report')"
                    @clickedButton="generateReport()"
                />
              </v-col>
            </v-row>
          </v-form>
          </v-col>

          <v-btn icon @click="closeDialog" class="d-none d-md-block mr-3 mt-3">
            <v-icon large color="primary">close</v-icon>
          </v-btn>

          <v-col class="pa-0 process-sidebar d-none d-md-block">
            <ProcessDocuSidebar ref="processDocuSidebar" @viewDocumentationFromSidebar="viewDocumentationFromSidebar"/>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <DocumentViewer hide-overlay :documentType="currentDocumentType" ref="documentViewer"/>
  </v-dialog>
</template>
<script>

import ProcessResultService from '@/modules/book/services/process/impl/process-result-service'
import ProcessStepService from '@/modules/book/services/process/impl/process-step-service'
import DocumentationService from "@/modules/core/services/documentation-service";
import Utils from "@/modules/core/services/utils";
import DataUtils from "@/modules/core/services/data-utils";

import ProcessResult from '@/modules/book/data/processResult';
import ProcessStepResult from '@/modules/book/data/processStepResult';
import ProcessStep from '@/modules/book/data/processStep';

import DataTable from '@/modules/core/components/DataTable/DataTable';
import LabelField from "@/modules/core/components/LabelField";
import TaskCard from "@/modules/book/components/TaskCard/TaskCard";
import ProcessDocuSidebar from "@/modules/book/components/Process/ProcessDocuSidebar"
import BaseButton from "@/modules/core/components/Base/BaseButton";
import DirtyConfirmButtonDialog from "@/modules/core/components/TwoButtonDialog/DirtyConfirmButtonDialog";

import DateUtils from "@/modules/core/services/date-utils";
import ProcessJobService from "@/modules/book/services/process/impl/process-job-service";
import PdfUtils from "@/modules/core/services/pdf-utils";
import LoadingDialog from "@/modules/core/components/Loader/LoadingDialog";
import {DocumentType} from "@/modules/book/data/document-type.js";

export default {
  name: 'ProcessResultDialog',
  components: {
    LoadingDialog,
    DataTable,
    LabelField,
    DocumentViewer: () => import('@/modules/core/components/DocumentViewer/DocumentViewer'),
    pdf: () => import('vue-pdf'),
    TaskCard,
    ProcessDocuSidebar,
    BaseButton,
    DirtyConfirmButtonDialog,
  },
  data() {
    return {
      processResult: new ProcessResult(),
      restProcessStepResults: [],
      restProcessSteps: [],
      allDocumentations: [],

      finishedOn: null,
      finishedOnAsDate: null,

      documentationTable: [],
      isFetchingProcessResult: true,
      dialog: false,
      openedPanel: null,
      currentDocumentType: DocumentType.STEP
    }
  },
  computed: {
    userHeaders() {
      return [
        {text: this.$t("common:user"), value: "contactedUsersNames"}
      ]
    },

    computedDateFormatted() {
      if (this.finishedOn) {
        return DateUtils.parseDateTimeForDisplay(this.finishedOn);
      }
      return "";
    }
  },
  methods: {
    generateReport() {
      this.$refs.loading.openDialog();
      ProcessJobService.generateReportByProcessJobResultId(this.processResult.id,
          (result) => {
            this.$refs.loading.closeDialog();
            PdfUtils.openPDFInNewTabSetFileType(result.data)
      }, (error) => {
            this.$refs.loading.closeDialog();
            this.$logger.error("ProcessResult could not be updated and report not being generated, because: " + error);
            Utils.showFailureSnackbar(this.$t("components:process-result-dialog.report-gen-failed"))
      });
    },

    // loads uploaded docues of failed process step result
    // will be called when an expansion panel header is clicked
    loadProcessStepResultDocus(item) {
      this.documentationTable = [];

      for(var i = 0; i < this.allDocumentations.length; i++) {
        for(var j = 0; j < item.documentation.length; j++) {
          if(this.allDocumentations[i].id == item.documentation[j]) {
            this.documentationTable.push(this.allDocumentations[i]);
            break
          }
        }
      }
    },

    // checks if user can add additional answer to processStepResult
    // flag is in according step, so we have to search for it
    canHaveComment(item) {
      for(var i = 0; i < this.restProcessSteps.length; i++) {
        if(item.step === this.restProcessSteps[i].id) {
          if(this.restProcessSteps[i].userAddsAnswer) {
            return true;
          } else {
            return false;
          }
        }
      }
    },

    // to init dialog with process result data
    openDialog(processResult) {
      if (!processResult.id) {
        this.$router.push('/404');
      }

      ProcessResultService.readFromAPI(processResult.id,
        (resp) => {
          this.processResult = DataUtils.parseDataToSingleObject(resp.data, ProcessResult);
          this.finishedOn = this.processResult.finishedOn;

          for (var i = 0; i < this.processResult.stepResults.length; i++) {
            this.restProcessStepResults.push(DataUtils.parseDataToSingleObject(this.processResult.stepResults[i], ProcessStepResult));

            for(var j = 0; j < this.processResult.stepResults[i].documentation.length; j++) {
              DocumentationService.readInfoFromApi(this.processResult.stepResults[i].documentation[j],
                (resp) => {
                  DocumentationService.getDocByTypeAndId(
                      resp.data.id,
                      resp.data.fileType,
                      resp.data.documentType,
                      this.processResult.facility,
                    (res) => {
                      resp.data.src = res;

                      // fill docu sidebar
                        this.$refs.processDocuSidebar.docuDocumentation.push(resp.data)

                      this.allDocumentations.push(resp.data)
                    },
                    () => {
                      Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:document_plural')}))
                    }
                  )
                },
                () => {
                  Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:document_plural')}))
                }
              )
            }

            ProcessStepService.readFromAPI(this.processResult.stepResults[i].step,
              (res) => {
                this.restProcessSteps.push(DataUtils.parseDataToSingleObject(res.data, ProcessStep))
              }
            )
          }

          this.isFetchingProcessResult = false;
          this.dialog = true;
        },
        (error) => {
          this.$logger.error("Failed to read process result, because: " + error);
          this.$router.push('/404')
        }
      )
    },

    // close and clean dialog data
    closeDialog() {
      this.processResult = new ProcessResult();
      this.restProcessStepResults = [];
      this.restProcessSteps = [];
      this.finishedOn = null;
      this.allDocumentations = [];
      this.documentationTable = [];
      this.isFetchingProcessResult = true;
      this.dialog = false;
      this.openedPanel = null;
      this.$refs.processDocuSidebar.clearDocumentatinos();
    },

    downloadDocumentation(data) {
      DocumentationService.downloadFile(data.id, data.fileName, data.fileType, this.processResult.facility, data.documentType);
    },

    // view uploaded documentations in documentation viewer
    viewDocumentation(data) {
      this.$refs.documentViewer.initViewerById(this.documentationTable, data.id, this.processResult.facility, data.documentType)
    },

    viewDocumentationFromSidebar(data) {
      this.$refs.documentViewer.initViewerById(this.allDocumentations, data.id, this.processResult.facility, data.documentType)
    },

    updateProcessStepResult(item) {
      if(item.success) {
        item.answer = "";
        item.failureText = "";
        item.additionalAnswer = "";
        item.documentation = [];
      }

      ProcessResultService.putStepResultFromResultToAPI(this.processResult.id, item,
        () => {
          this.openedPanel = null;
          Utils.showSuccessSnackbar(this.$t('snackbar:edit-success', {name: this.$t('components:process-result-dialog.snackbar')}));
        },
        () => {
          Utils.showFailureSnackbar(this.$t('snackbar:edit-failure', {name: this.$t('components:process-result-dialog.snackbar')}))
        }
      )
    },

    taskStatusText(item) {
      if(item.success) {
        return this.$t('common:success');
      } else {
        return this.$t('components:process-result-dialog.failure-badge');
      }
    },

    getAdditionalNames(processResult) {
      return [
        {name: processResult.buildingName, asButton: false},
        {name: processResult.facilityName, asButton: false}
      ];
    }
  }
}
</script>
<style lang="scss">
.process-result-dialog {
  /*
  had to set z-index of dialog and overlay manually here
  because here we have a single case of dialog over dialog
*/
  .v-dialog__content.v-dialog__content--active {
    z-index: 500 !important;
  }

  .overview-card {
    background-color: #fff;
  }

  .result-card {
    background-color: rgba(246, 247, 255, 0.97)!important;
  }

  .border-left-success--true {
    border-left: 5px solid var(--v-scaleGreen-base)!important;
  }

  .border-left-success--false {
    border-left: 5px solid var(--v-scaleRed-base)!important;
  }

  .v-expansion-panel {
    background-color: transparent!important;

    .v-expansion-panel-header {
      background-color: #fff;
    }

    .v-expansion-panel-content {
      background-color: #fff;

      .v-expansion-panel-content__wrap {
        padding: 16px 24px;
      }
    }
  }

  .docu-preview {
    border: 1px solid #ddd; /* Gray border */
    border-radius: 4px;  /* Rounded border */
    padding: 5px; /* Some padding */
    width: 200px;
    cursor: pointer;
  }
}
</style>